import React, { useState, useRef, useEffect, useCallback } from 'react';
import Button from '../components/Button';
import { useError } from '../contexts/ErrorContext';
import './SerialMonitorPage.css';

// Common baud rates
const BAUD_RATES = [
  9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600
];

const SerialMonitorPage = () => {
  const { addError } = useError();
  
  const [isSupported, setIsSupported] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [baudRate, setBaudRate] = useState(115200);
  const [logs, setLogs] = useState([]);
  const [portInfo, setPortInfo] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [inputValue, setInputValue] = useState('');
  
  const portRef = useRef(null);
  const readerRef = useRef(null);
  const writerRef = useRef(null);
  const logsEndRef = useRef(null);
  const textDecoderRef = useRef(new TextDecoder());

  // Check WebSerial support
  useEffect(() => {
    setIsSupported('serial' in navigator);
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const addLog = useCallback((text, type = 'rx') => {
    const timestamp = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    
    // Split by newlines and add each line separately
    const lines = text.split('\n');
    const newLogs = lines
      .filter(line => line.length > 0)
      .map(line => ({
        id: Date.now() + Math.random(),
        timestamp,
        text: line,
        type
      }));
    
    if (newLogs.length > 0) {
      setLogs(prev => {
        // Keep only last 10000 lines to prevent memory issues
        const combined = [...prev, ...newLogs];
        return combined.slice(-10000);
      });
    }
  }, []);

  const readLoop = useCallback(async () => {
    if (!portRef.current || !portRef.current.readable) return;
    
    try {
      readerRef.current = portRef.current.readable.getReader();
      
      while (true) {
        const { value, done } = await readerRef.current.read();
        if (done) {
          break;
        }
        if (value) {
          const text = textDecoderRef.current.decode(value);
          addLog(text, 'rx');
        }
      }
    } catch (error) {
      if (error.name !== 'NetworkError' && !error.message.includes('cancelled')) {
        console.error('Read error:', error);
        addError(`Serial read error: ${error.message}`);
      }
    } finally {
      if (readerRef.current) {
        readerRef.current.releaseLock();
        readerRef.current = null;
      }
    }
  }, [addLog, addError]);

  const connectPort = async () => {
    if (!isSupported) {
      addError('WebSerial is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    setIsConnecting(true);
    
    try {
      // Request port from user
      const port = await navigator.serial.requestPort();
      portRef.current = port;
      
      // Get port info
      const info = port.getInfo();
      setPortInfo({
        usbVendorId: info.usbVendorId?.toString(16).toUpperCase().padStart(4, '0'),
        usbProductId: info.usbProductId?.toString(16).toUpperCase().padStart(4, '0')
      });
      
      // Open the port
      await port.open({ baudRate });
      
      setIsConnected(true);
      addLog(`Connected at ${baudRate} baud`, 'system');
      
      // Start reading
      readLoop();
      
      // Set up writer
      if (port.writable) {
        writerRef.current = port.writable.getWriter();
      }
      
    } catch (error) {
      if (error.name === 'NotFoundError') {
        // User cancelled the port selection
        console.log('Port selection cancelled');
      } else {
        console.error('Connection error:', error);
        addError(`Failed to connect: ${error.message}`);
      }
      portRef.current = null;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectPort = useCallback(async () => {
    try {
      // Cancel the reader
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current.releaseLock();
        readerRef.current = null;
      }
      
      // Release the writer
      if (writerRef.current) {
        writerRef.current.releaseLock();
        writerRef.current = null;
      }
      
      // Close the port
      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
      }
      
      setIsConnected(false);
      setPortInfo(null);
      addLog('Disconnected', 'system');
      
    } catch (error) {
      console.error('Disconnect error:', error);
      addError(`Failed to disconnect: ${error.message}`);
    }
  }, [addLog, addError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectPort();
    };
  }, [disconnectPort]);

  const sendData = async () => {
    if (!writerRef.current || !inputValue.trim()) return;
    
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(inputValue + '\n');
      await writerRef.current.write(data);
      addLog(inputValue, 'tx');
      setInputValue('');
    } catch (error) {
      console.error('Send error:', error);
      addError(`Failed to send: ${error.message}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendData();
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const exportLogs = () => {
    const content = logs.map(log => {
      const prefix = showTimestamps ? `[${log.timestamp}] ` : '';
      const direction = log.type === 'tx' ? '>> ' : log.type === 'system' ? '-- ' : '';
      return `${prefix}${direction}${log.text}`;
    }).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `serial_log_${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isSupported) {
    return (
      <div className="serial-monitor-page">
        <div className="serial-monitor-header">
          <div className="serial-monitor-title">
            <h2>Serial Monitor</h2>
          </div>
        </div>
        <div className="serial-monitor-unsupported">
          <div className="unsupported-icon">⚠️</div>
          <h3>WebSerial Not Supported</h3>
          <p>
            Your browser does not support the WebSerial API.
            Please use <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong> to use this feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="serial-monitor-page">
      <div className="serial-monitor-header">
        <div className="serial-monitor-title">
          <h2>Serial Monitor</h2>
          {portInfo && (
            <span className="port-info">
              VID: {portInfo.usbVendorId || 'N/A'} | PID: {portInfo.usbProductId || 'N/A'}
            </span>
          )}
        </div>
        <div className="serial-monitor-controls">
          <div className="control-group">
            <label>Baud Rate:</label>
            <select 
              value={baudRate} 
              onChange={(e) => setBaudRate(Number(e.target.value))}
              disabled={isConnected}
              className="baud-select"
            >
              {BAUD_RATES.map(rate => (
                <option key={rate} value={rate}>{rate}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group checkbox-group">
            <label>
              <input 
                type="checkbox" 
                checked={showTimestamps}
                onChange={(e) => setShowTimestamps(e.target.checked)}
              />
              Timestamps
            </label>
            <label>
              <input 
                type="checkbox" 
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
              />
              Auto-scroll
            </label>
          </div>
          
          <div className="control-group button-group">
            <Button 
              variant="outline" 
              size="small"
              onClick={clearLogs}
            >
              Clear
            </Button>
            <Button 
              variant="outline" 
              size="small"
              onClick={exportLogs}
              disabled={logs.length === 0}
            >
              Export
            </Button>
            {!isConnected ? (
              <Button 
                variant="primary" 
                size="small"
                onClick={connectPort}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            ) : (
              <Button 
                variant="danger" 
                size="small"
                onClick={disconnectPort}
              >
                Disconnect
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="serial-monitor-terminal">
        <div className="terminal-output">
          {logs.length === 0 ? (
            <div className="terminal-placeholder">
              {isConnected 
                ? 'Waiting for data...' 
                : 'Click "Connect" to select a serial device and start monitoring'}
            </div>
          ) : (
            logs.map(log => (
              <div 
                key={log.id} 
                className={`terminal-line ${log.type}`}
              >
                {showTimestamps && (
                  <span className="terminal-timestamp">[{log.timestamp}]</span>
                )}
                {log.type === 'tx' && <span className="terminal-direction">&gt;&gt;</span>}
                {log.type === 'system' && <span className="terminal-direction">--</span>}
                <span className="terminal-text">{log.text}</span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>

      <div className="serial-monitor-input">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isConnected ? "Type message and press Enter to send..." : "Connect to a device first"}
          disabled={!isConnected}
          className="serial-input"
        />
        <Button 
          variant="primary"
          onClick={sendData}
          disabled={!isConnected || !inputValue.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default SerialMonitorPage;
